/**
 * ANEMOIA BOY — handheld falling-block game popup (custom).
 *
 * Three steps, by design:
 *   1. A quiet ask ("peak nostalgia?") after a delay. NO closes it and costs
 *      the visitor nothing.
 *   2. YES boots a handheld: original title screen, local leaderboard, and a
 *      full falling-block game rendered at the real 160x144 LCD resolution.
 *      Clear the target score and the reward unlocks mid-run — the visitor keeps
 *      playing until they top out, which is the point: the prize is earned and
 *      the run is theirs to push.
 *   3. Top out with the reward unlocked and a rocket launches off the LCD,
 *      revealing the discount. Claim hands off to the Omnisend popup form via
 *      the documented custom trigger, exactly as the slot machine does — no API
 *      key, no backend of our own.
 *
 * Top out WITHOUT the reward and it is a plain GAME OVER and a retry. That is
 * what keeps the unlock worth anything.
 *
 * ON THE ARTWORK: the shapes, the gravity curve and the 40/100/300/1200 scoring
 * are the genre's common mechanics, which are not anyone's property. Everything
 * drawn here — wordmark, title screen, block textures, rocket, the shell — is
 * ours. No third-party name, logo, screen or music is reproduced. Keep it that
 * way if you extend this.
 *
 * Frequency, delay, prize copy and the Omnisend form id all arrive on data-*
 * from theme settings (see snippets/anemoia-gameboy.liquid).
 */

const STORE_KEY = 'anemoia:gb';
/**
 * Time already spent on the site this session, in ms. Like the slot machine,
 * the delay is a budget across the whole visit rather than per page — someone
 * browsing five pages for ten seconds each should still reach it. Per-tab
 * (sessionStorage), so a fresh visit starts a fresh countdown.
 */
const ELAPSED_KEY = 'anemoia:gb-elapsed';
const SCORES_KEY = 'anemoia:gb-scores';
/** The storefront's shared sound preference. We READ it, but never write it. */
const SOUND_PREF_KEY = 'anemoia:sound';
/** The console's own switch, so the game's audio is independent of the site's. */
const GAME_SOUND_KEY = 'anemoia:gb-sound';
const CLOCK_MS = 250;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------- the screen -- */

/** Native DMG-class resolution. The canvas backing store is exactly this and
 *  CSS scales it up with image-rendering:pixelated, so every pixel stays square
 *  and the whole thing is resolution-independent for free. */
const W = 160;
const H = 144;

const COLS = 10;
const ROWS = 18;
const CELL = 7;
/** Playfield origin, leaving a 1px frame and a stats panel down the right. */
const FX = 6;
const FY = 9;
const PANEL_X = 84;

/** 60Hz logic tick. Every duration in this file is in frames, like the original. */
const STEP = 1000 / 60;
/** Never simulate more than this after a tab-switch stall. */
const MAX_CATCHUP = 5;

/* -------------------------------------------------------------- the font -- */

/**
 * 5x7 bitmap font, one binary literal per row. Hand-drawn rather than a webfont
 * because at 7px a real typeface renders as grey mush — this stays hard-edged
 * at any scale, which is the entire look.
 */
const FONT = {
  A: [0b01110, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001],
  B: [0b11110, 0b10001, 0b10001, 0b11110, 0b10001, 0b10001, 0b11110],
  C: [0b01110, 0b10001, 0b10000, 0b10000, 0b10000, 0b10001, 0b01110],
  D: [0b11100, 0b10010, 0b10001, 0b10001, 0b10001, 0b10010, 0b11100],
  E: [0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b11111],
  F: [0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b10000],
  G: [0b01110, 0b10001, 0b10000, 0b10111, 0b10001, 0b10001, 0b01111],
  H: [0b10001, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001],
  I: [0b01110, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110],
  J: [0b00111, 0b00010, 0b00010, 0b00010, 0b00010, 0b10010, 0b01100],
  K: [0b10001, 0b10010, 0b10100, 0b11000, 0b10100, 0b10010, 0b10001],
  L: [0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b11111],
  M: [0b10001, 0b11011, 0b10101, 0b10101, 0b10001, 0b10001, 0b10001],
  N: [0b10001, 0b11001, 0b10101, 0b10011, 0b10001, 0b10001, 0b10001],
  O: [0b01110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110],
  P: [0b11110, 0b10001, 0b10001, 0b11110, 0b10000, 0b10000, 0b10000],
  Q: [0b01110, 0b10001, 0b10001, 0b10001, 0b10101, 0b10010, 0b01101],
  R: [0b11110, 0b10001, 0b10001, 0b11110, 0b10100, 0b10010, 0b10001],
  S: [0b01111, 0b10000, 0b10000, 0b01110, 0b00001, 0b00001, 0b11110],
  T: [0b11111, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100],
  U: [0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110],
  V: [0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100],
  W: [0b10001, 0b10001, 0b10001, 0b10101, 0b10101, 0b11011, 0b10001],
  X: [0b10001, 0b10001, 0b01010, 0b00100, 0b01010, 0b10001, 0b10001],
  Y: [0b10001, 0b10001, 0b01010, 0b00100, 0b00100, 0b00100, 0b00100],
  Z: [0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b10000, 0b11111],
  0: [0b01110, 0b10001, 0b10011, 0b10101, 0b11001, 0b10001, 0b01110],
  1: [0b00100, 0b01100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110],
  2: [0b01110, 0b10001, 0b00001, 0b00010, 0b00100, 0b01000, 0b11111],
  3: [0b11111, 0b00010, 0b00100, 0b00010, 0b00001, 0b10001, 0b01110],
  4: [0b00010, 0b00110, 0b01010, 0b10010, 0b11111, 0b00010, 0b00010],
  5: [0b11111, 0b10000, 0b11110, 0b00001, 0b00001, 0b10001, 0b01110],
  6: [0b00110, 0b01000, 0b10000, 0b11110, 0b10001, 0b10001, 0b01110],
  7: [0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b01000, 0b01000],
  8: [0b01110, 0b10001, 0b10001, 0b01110, 0b10001, 0b10001, 0b01110],
  9: [0b01110, 0b10001, 0b10001, 0b01111, 0b00001, 0b00010, 0b01100],
  ' ': [0, 0, 0, 0, 0, 0, 0],
  '.': [0, 0, 0, 0, 0, 0b01100, 0b01100],
  ',': [0, 0, 0, 0, 0b00110, 0b00110, 0b01100],
  ':': [0, 0b01100, 0b01100, 0, 0b01100, 0b01100, 0],
  '!': [0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0, 0b00100],
  '?': [0b01110, 0b10001, 0b00001, 0b00010, 0b00100, 0, 0b00100],
  '-': [0, 0, 0, 0b11111, 0, 0, 0],
  _: [0, 0, 0, 0, 0, 0, 0b11111],
  "'": [0b00100, 0b00100, 0, 0, 0, 0, 0],
  '%': [0b11001, 0b11010, 0b00010, 0b00100, 0b01000, 0b01011, 0b10011],
  '+': [0, 0b00100, 0b00100, 0b11111, 0b00100, 0b00100, 0],
  '/': [0b00001, 0b00010, 0b00010, 0b00100, 0b01000, 0b01000, 0b10000],
  '*': [0, 0b10101, 0b01110, 0b11111, 0b01110, 0b10101, 0],
  '<': [0b01000, 0b00100, 0b00010, 0b00001, 0b00010, 0b00100, 0b01000],
  '>': [0b00010, 0b00100, 0b01000, 0b10000, 0b01000, 0b00100, 0b00010],
  '(': [0b00010, 0b00100, 0b01000, 0b01000, 0b01000, 0b00100, 0b00010],
  ')': [0b01000, 0b00100, 0b00010, 0b00010, 0b00010, 0b00100, 0b01000],
};
const GLYPH_W = 5;
const GLYPH_H = 7;
/** 1px of air between glyphs, so one character advances 6px at scale 1. */
const TRACK = 1;

/* ------------------------------------------------------------ the pieces -- */

/**
 * Rotation states written out longhand rather than derived by matrix maths.
 * The classic handheld system is not a pure rotation: I, S and Z have two
 * states (so they oscillate rather than walk), O has one, and the rest have
 * four. Deriving it would produce a subtly different, more modern-feeling game.
 *
 * `style` picks one of three block textures, so the stack stays readable in
 * four shades of green the way the original's shading did.
 */
const PIECE_DEFS = {
  I: {
    style: 0,
    states: [
      ['....', '####', '....', '....'],
      ['..#.', '..#.', '..#.', '..#.'],
    ],
  },
  O: {
    style: 1,
    states: [['....', '.##.', '.##.', '....']],
  },
  T: {
    style: 2,
    states: [
      ['....', '###.', '.#..', '....'],
      ['.#..', '##..', '.#..', '....'],
      ['.#..', '###.', '....', '....'],
      ['.#..', '.##.', '.#..', '....'],
    ],
  },
  S: {
    style: 0,
    states: [
      ['....', '.##.', '##..', '....'],
      ['#...', '##..', '.#..', '....'],
    ],
  },
  Z: {
    style: 1,
    states: [
      ['....', '##..', '.##.', '....'],
      ['..#.', '.##.', '.#..', '....'],
    ],
  },
  J: {
    style: 2,
    states: [
      ['....', '###.', '..#.', '....'],
      ['.#..', '.#..', '##..', '....'],
      ['#...', '###.', '....', '....'],
      ['.##.', '.#..', '.#..', '....'],
    ],
  },
  L: {
    style: 0,
    states: [
      ['....', '###.', '#...', '....'],
      ['##..', '.#..', '.#..', '....'],
      ['..#.', '###.', '....', '....'],
      ['.#..', '.#..', '.##.', '....'],
    ],
  },
};

const PIECE_KEYS = Object.keys(PIECE_DEFS);

/** Parse the ASCII states once into [x, y] cell lists. */
const PIECES = {};
PIECE_KEYS.forEach((key) => {
  const def = PIECE_DEFS[key];
  PIECES[key] = {
    style: def.style,
    states: def.states.map((rows) => {
      const cells = [];
      rows.forEach((row, y) => {
        row.split('').forEach((char, x) => {
          if (char === '#') cells.push([x, y]);
        });
      });
      return cells;
    }),
  };
});

/**
 * Frames per row of gravity, indexed by level. The classic curve: forgiving for
 * the first four levels, then a hard shoulder around 9 where the game stops
 * being a puzzle and starts being a reflex test.
 */
const GRAVITY = [53, 49, 45, 41, 37, 33, 28, 22, 17, 11, 10, 9, 8, 7, 6, 6, 5, 5, 4, 4, 3];
/** Line-clear payouts, multiplied by (level + 1). Index = rows cleared. */
const LINE_SCORE = [0, 40, 100, 300, 1200];
const LINES_PER_LEVEL = 10;

/** Held-direction auto-repeat: a long first beat, then a fast walk. */
const DAS_DELAY = 16;
const DAS_REPEAT = 6;
/*
 * There is deliberately NO lock delay. A piece commits the moment gravity
 * cannot move it down, the way the original did. That still leaves one full
 * gravity interval to slide it after it lands (0.88s at level 0, 0.05s at level
 * 20) — the grace comes from the drop clock itself, not from a separate timer.
 * Holding soft drop shortens that interval to 2 frames, so pushing down commits
 * the piece almost immediately. That is the intended trade.
 */

/** How long the cleared rows blink before the stack collapses. */
const CLEAR_FRAMES = 24;

/**
 * Characters available when entering initials on the high-score board. Three
 * slots, spun one at a time — every glyph here exists in FONT, and the trailing
 * space lets someone enter a one or two letter name.
 */
const ENTRY_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 '.split('');

/**
 * How many characters a name gets. Five fits the LCD at the big entry scale
 * (5 x 18px = 87px of a 160px screen) and leaves the leaderboard columns
 * aligned, since board names are padded to seven either way.
 */
const ENTRY_SLOTS = 5;

/**
 * The two music loops, as frequencies in Hz — written here rather than loaded,
 * so there is no audio file to host, license or wait on, and the music comes
 * out of the same square oscillator as the sound effects.
 *
 * Both are ours. The gameplay loop arpeggiates A minor - F - C - G, four beats
 * to a chord: the most common progression in popular music and about as close
 * to unowned musical furniture as exists. That is the point — it has to sit
 * under a game for several minutes without demanding attention, and anything
 * with a hook would wear out on the second loop. `0` is a rest.
 */
/*
 * One knob for the whole console's loudness. Every per-effect gain below is a
 * relative mix value; this is what turns them into an actual level.
 *
 * It exists because the original mix was written on desktop speakers, where
 * -30dB is a pleasant background chirp and on a phone speaker is nothing at
 * all. The limiter on the bus catches the peaks, so this can be pushed without
 * clipping. Raise it if the handheld is still too quiet in the hand; that is
 * the intended way to tune it.
 */
const MASTER_GAIN = 2.5;

const MUSIC = {
  title: {
    step: 0.16,
    leadGain: 0.03,
    bassGain: 0.045,
    lead: [440, 523, 659, 523, 494, 587, 740, 587],
    bass: [110, 0, 110, 0, 123, 0, 123, 0],
  },
  game: {
    step: 0.15,
    leadGain: 0.025,
    bassGain: 0.04,
    lead: [
      440, 523, 659, 523, // Am
      440, 523, 698, 523, // F
      392, 523, 659, 523, // C
      392, 494, 587, 494, // G
    ],
    bass: [110, 0, 110, 0, 87, 0, 87, 0, 131, 0, 131, 0, 98, 0, 98, 0],
  },
};

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])';

/* ------------------------------------------------------------- utilities -- */

/**
 * The game is AUDIBLE BY DEFAULT, unlike the rest of the storefront.
 *
 * A handheld with no sound is missing half of what it is, and the popup only
 * ever starts from a deliberate "yes" — which is both a user gesture (so
 * autoplay policy is satisfied) and consent to the thing being a game. The
 * background page is silenced for the duration, so nothing ends up doubled.
 *
 * The order of precedence:
 *   1. The console's own SOUND switch, once the visitor has touched it.
 *   2. Otherwise on — unless the visitor has explicitly muted the whole
 *      storefront, which is a deliberate act we do not override.
 *
 * Deliberately a SEPARATE key from the site-wide preference: toggling sound in
 * here must not silently rewrite what the visitor chose for the storefront, and
 * keeping them apart also stops the world layer from reacting to our writes and
 * un-muting its own videos underneath us.
 */
function soundOn() {
  try {
    const own = localStorage.getItem(GAME_SOUND_KEY);
    if (own === 'on') return true;
    if (own === 'off') return false;
    return localStorage.getItem(SOUND_PREF_KEY) !== 'off';
  } catch (error) {
    return true;
  }
}

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
}

function writeState(patch) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ ...readState(), ...patch }));
  } catch (error) {
    /* storage unavailable (private mode); the popup just reappears next visit. */
  }
}

/** Zero-pad a score to a fixed width, the way a scoreboard always has. */
function pad(value, width) {
  const text = String(Math.max(0, Math.floor(value)));
  return text.length >= width ? text : '0'.repeat(width - text.length) + text;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/* ================================================================ element == */

class AnemoiaGameboy extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-agb-dialog]');
    this.canvas = this.querySelector('[data-agb-lcd]');
    this.statusEl = this.querySelector('[data-agb-status]');
    this.claimBtn = this.querySelector('[data-agb-claim]');
    this.soundBtn = this.querySelector('[data-agb-sound]');
    this.designMode = this.dataset.designMode === 'true';

    // Music is separable from effects: a merchant may want the blips without a
    // loop running under them.
    this.musicOn = this.dataset.music !== 'false';
    this.unlockAt = Number(this.dataset.unlockAt) || 100;
    this.prize = (this.dataset.prize || '10% OFF').toUpperCase();
    this.gameName = (this.dataset.gameName || 'STACK').toUpperCase();
    this.gameSuper = (this.dataset.gameSuper || 'ANEMOIA').toUpperCase();

    if (!this.canvas || !this.canvas.getContext) return;
    if (!this.shouldShow()) return;

    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.ctx.imageSmoothingEnabled = false;

    this.readPalette();
    this.wire();

    // In the theme editor, open straight away and skip the frequency rules so
    // the merchant can actually style the thing. ?resetpopup does the same, on
    // the reasoning that anyone who asked for a reset wants to see it now
    // rather than sit through the delay a dozen times.
    if (this.designMode || this.resetRequested) {
      this.openTimer = window.setTimeout(() => this.open(), 400);
      return;
    }
    this.startClock();
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
    if (this.clock) window.clearInterval(this.clock);
    if (this.raf) cancelAnimationFrame(this.raf);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('anemoia:mode-change', this.onModeChange);
    // Torn down while open (a section re-render in the editor): never leave the
    // page muted with no popup left to explain why.
    this.stopMusic();
    this.unhushPage();
  }

  /* ------------------------------------------------------------ gating -- */

  shouldShow() {
    if (this.designMode) return true;

    try {
      const params = new URLSearchParams(window.location.search);

      // An explicit ?nopopup on the URL wins over everything (QA, ad landings).
      if (params.has('nopopup')) return false;

      // ?resetpopup is its QA counterpart: wipe the frequency state and show
      // again immediately, so walking the whole flow another time is a bookmark
      // rather than a console paste. ?resetpopup=all also clears the top scores.
      if (params.has('resetpopup')) {
        this.resetState(params.get('resetpopup') === 'all');
        this.resetRequested = true;
        return true;
      }
    } catch (error) {
      /* ignore */
    }

    const state = readState();
    if (state.claimed) return false; // already took the prize: never nag again

    const days = Number(this.dataset.repeatDays);
    if (state.seen && days > 0) {
      if (Date.now() - Number(state.seen) < days * 864e5) return false;
    }
    return true;
  }

  /**
   * Forget this visitor entirely: the claim flag, the last-seen stamp and the
   * banked delay. Only ever reached from ?resetpopup, so it cannot fire on a
   * real visit. Pass true to drop the leaderboard too.
   */
  resetState(alsoScores) {
    try {
      localStorage.removeItem(STORE_KEY);
      if (alsoScores) localStorage.removeItem(SCORES_KEY);
    } catch (error) {
      /* storage unavailable; nothing to clear anyway. */
    }
    try {
      sessionStorage.removeItem(ELAPSED_KEY);
    } catch (error) {
      /* ditto */
    }
  }

  /* ------------------------------------------------------------- clock -- */

  readElapsed() {
    try {
      return Number(sessionStorage.getItem(ELAPSED_KEY)) || 0;
    } catch (error) {
      return 0;
    }
  }

  writeElapsed(ms) {
    try {
      sessionStorage.setItem(ELAPSED_KEY, String(Math.round(ms)));
    } catch (error) {
      /* storage unavailable; the countdown just restarts on the next page. */
    }
  }

  /**
   * Run down the remaining budget. Time in a background tab does not count —
   * the visitor is not browsing, and counting it would open the popup behind a
   * tab they are not looking at.
   */
  startClock() {
    const total = (Number(this.dataset.delay) || 15) * 1000;
    let elapsed = this.readElapsed();

    if (elapsed >= total) {
      this.open();
      return;
    }

    let last = performance.now();
    this.clock = window.setInterval(() => {
      const now = performance.now();
      const delta = now - last;
      last = now;
      if (document.hidden) return;

      elapsed += delta;
      this.writeElapsed(elapsed);
      if (elapsed >= total) {
        window.clearInterval(this.clock);
        this.clock = null;
        this.open();
      }
    }, CLOCK_MS);
  }

  /* -------------------------------------------------------------- wire -- */

  wire() {
    this.onKeydown = (event) => this.handleKey(event, true);
    this.onKeyup = (event) => this.handleKey(event, false);
    this.onModeChange = () => {
      this.readPalette();
      if (this.state) this.draw();
    };

    this.querySelectorAll('[data-agb-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    // The ask. NO is a real answer and costs the visitor nothing — it closes
    // and, unlike a dismissal mid-game, is remembered for the full repeat window.
    this.querySelector('[data-agb-no]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-agb-yes]')?.addEventListener('click', () => this.boot());

    this.claimBtn?.addEventListener('click', () => this.claim());
    this.soundBtn?.addEventListener('click', () => this.toggleSound());
    this.syncSoundBtn();
    this.wireButtons();
    this.wireForm();
  }

  /**
   * The shell's own controls. Pointer events rather than click so a held
   * direction repeats — on a touch screen these ARE the keyboard, and a D-pad
   * that only fires on release is not a D-pad.
   */
  wireButtons() {
    this.held = {};
    this.querySelectorAll('[data-agb-btn]').forEach((btn) => {
      const name = btn.dataset.agbBtn;

      const press = (event) => {
        event.preventDefault();
        // Pointer capture keeps a slid thumb on the button it started on,
        // rather than dropping the input the moment it leaves the hit box.
        if (event.pointerId !== undefined && btn.setPointerCapture) {
          try {
            btn.setPointerCapture(event.pointerId);
          } catch (error) {
            /* capture unavailable; the up handler below still fires */
          }
        }
        btn.classList.add('is-down');
        this.input(name, true);
      };
      const release = (event) => {
        event.preventDefault();
        btn.classList.remove('is-down');
        this.input(name, false);
      };

      btn.addEventListener('pointerdown', press);
      btn.addEventListener('pointerup', release);
      btn.addEventListener('pointercancel', release);
      // Belt and braces for browsers that drop the pointer without cancelling.
      btn.addEventListener('lostpointercapture', release);
    });
  }

  /* -------------------------------------------------------------- open -- */

  open() {
    if (this.clock) {
      window.clearInterval(this.clock);
      this.clock = null;
    }
    // Spent. If the popup is ever due again it should earn its next appearance
    // with a fresh countdown rather than opening instantly.
    this.writeElapsed(0);

    this.hidden = false;
    this.lastFocus = document.activeElement;
    requestAnimationFrame(() => this.classList.add('is-open'));

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('keyup', this.onKeyup);
    document.addEventListener('anemoia:mode-change', this.onModeChange);

    this.hushPage();
    this.syncSoundBtn();

    this.querySelector('[data-agb-yes]')?.focus({ preventScroll: true });
    if (!this.designMode) writeState({ seen: Date.now() });
  }

  /**
   * Silence the page underneath for as long as the popup is up, so the game is
   * the only thing making noise.
   *
   * MUTE, never pause: the storefront runs autoplaying background loops for
   * their visuals, and pausing them would freeze the world behind the scrim.
   * Only elements that were actually audible are touched, and each is restored
   * on close — a video the site had already muted stays muted, so we never hand
   * back a page noisier than we found it.
   */
  hushPage() {
    // Guarded, and it must stay that way. Hushing twice would find everything
    // already muted, record nothing, and leave unhushPage() with no way to put
    // the page back — silently muting the storefront for good. open() can fire
    // more than once (theme editor reloads), so this is reachable.
    if (this.isHushed) return;
    this.isHushed = true;
    this.hushed = [];

    try {
      document.querySelectorAll('video, audio').forEach((el) => {
        if (this.contains(el) || el.muted) return;
        this.hushed.push(el);
        el.muted = true;
      });
    } catch (error) {
      /* nothing to silence */
    }
  }

  unhushPage() {
    if (!this.isHushed) return;
    this.isHushed = false;
    (this.hushed || []).forEach((el) => {
      el.muted = false;
    });
    this.hushed = [];
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('anemoia:mode-change', this.onModeChange);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.stopMusic();
    this.unhushPage(); // hand the page back exactly as audible as we found it

    window.setTimeout(() => {
      this.hidden = true;
    }, 450);
    if (this.lastFocus && this.lastFocus.focus) {
      this.lastFocus.focus({ preventScroll: true });
    }
  }

  showStep(name) {
    this.querySelectorAll('[data-agb-step]').forEach((step) => {
      step.classList.toggle('is-active', step.dataset.agbStep === name);
    });

    // Prefer an explicit landing target where the step declares one. Focusing
    // the first control on the console step would put a focus ring on a D-pad
    // arm, which reads as a broken button rather than as focus.
    const explicit = this.querySelector(`[data-agb-step="${name}"] [data-agb-focus]`);
    if (explicit) {
      explicit.focus({ preventScroll: true });
      return;
    }

    const scoped = FOCUSABLE.split(', ')
      .map((sel) => `[data-agb-step="${name}"] ${sel}`)
      .join(', ');
    this.querySelector(scoped)?.focus({ preventScroll: true });
  }

  say(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }

  trapFocus(event) {
    const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------- input -- */

  /**
   * Keyboard maps onto the same eight virtual buttons the shell exposes, so
   * there is exactly one input path to reason about. Arrows and space are
   * swallowed while the console is up — otherwise every soft drop also scrolls
   * whatever is behind the popup.
   */
  handleKey(event, down) {
    if (event.key === 'Escape' && down) {
      this.close();
      return;
    }
    if (event.key === 'Tab' && down) {
      this.trapFocus(event);
      return;
    }
    if (!this.booted) return;

    /*
     * A real control has focus — the SOUND switch, CLAIM, the close X, a form
     * field. Leave it alone. Without this, Enter on a focused button is mapped
     * to START and preventDefault'ed, so the button never activates and the
     * game pauses instead. That is what made the blocks stop the moment anyone
     * used the sound switch.
     */
    const focused = event.target;
    if (
      focused &&
      focused.closest &&
      focused.closest('button:not([data-agb-btn]), a[href], input, textarea, select')
    ) {
      return;
    }

    const map = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowDown: 'down',
      ArrowUp: 'up',
      KeyA: 'left',
      KeyD: 'right',
      KeyS: 'down',
      KeyW: 'up',
      KeyZ: 'b',
      KeyX: 'a',
      Space: 'a',
      Enter: 'start',
      ShiftLeft: 'select',
    };
    const name = map[event.code];
    if (!name) return;

    event.preventDefault();
    if (down && event.repeat) return; // our own DAS owns repeats, not the OS
    this.input(name, down);

    const btn = this.querySelector(`[data-agb-btn="${name}"]`);
    if (btn) btn.classList.toggle('is-down', down);
  }

  /** One virtual button changed state. Edge-triggered actions fire here; held
   *  directions are read by the game loop each frame. */
  input(name, down) {
    if (!this.held) this.held = {};
    const was = Boolean(this.held[name]);
    this.held[name] = down;
    if (!down || was) return;

    // Any press wakes the audio context — this is the gesture browsers want.
    this.resumeAudio();

    if (this.state === 'title') {
      if (name === 'start' || name === 'a') this.startGame();
      return;
    }
    if (this.state === 'over') {
      if (name === 'start' || name === 'a') this.toTitle();
      return;
    }
    if (this.state === 'entry') {
      // Up/down spins the current initial, left/right walks between the three,
      // A or START commits. B steps back rather than trapping anyone who
      // overshoots the slot they wanted.
      if (name === 'up') this.cycleLetter(1);
      else if (name === 'down') this.cycleLetter(-1);
      else if (name === 'right') this.entryPos = Math.min(ENTRY_SLOTS - 1, this.entryPos + 1);
      else if (name === 'left') this.entryPos = Math.max(0, this.entryPos - 1);
      else if (name === 'a' || name === 'start') this.confirmName();
      else if (name === 'b') this.entryPos = Math.max(0, this.entryPos - 1);
      return;
    }
    if (this.state !== 'play') return;

    if (name === 'left' || name === 'right') {
      this.move(name === 'left' ? -1 : 1);
      this.das = { dir: name === 'left' ? -1 : 1, timer: DAS_DELAY };
    } else if (name === 'a') {
      this.rotate(1);
    } else if (name === 'b') {
      this.rotate(-1);
    } else if (name === 'up') {
      this.hardDrop();
    } else if (name === 'start') {
      this.paused = !this.paused;
      this.say(this.paused ? 'Paused' : 'Playing');
    }
  }

  /* ============================================================== boot == */

  /**
   * YES on the ask. Swap to the console and run the power-on sequence: the
   * wordmark drops in, holds, chimes, and hands over to the title screen.
   */
  boot() {
    this.booted = true;
    this.showStep('console');
    this.say('Handheld powered on.');
    this.resumeAudio();

    this.state = 'boot';
    this.frame = 0;
    this.bootY = -18;
    this.chimed = false;
    this.loop();

    // Reduced motion: skip the drop, hold the logo briefly, move on.
    if (REDUCED_MOTION.matches) this.frame = 60;
  }

  toTitle() {
    this.state = 'title';
    this.frame = 0;
    this.scores = this.readScores();
    this.say('Title screen. Press start to play.');
    this.startMusic();
  }

  /* ------------------------------------------------------- leaderboard -- */

  /**
   * Local only, deliberately. A shared board needs a backend, and a backend
   * needs moderation the moment someone types into it — this is a popup, not a
   * service. Seeds come from theme settings so the board is never empty on a
   * first run and the visitor has something to beat.
   */
  readScores() {
    let mine = [];
    try {
      mine = JSON.parse(localStorage.getItem(SCORES_KEY) || '[]') || [];
    } catch (error) {
      mine = [];
    }

    /*
     * Pull out NAME|SCORE pairs wherever they appear rather than trusting the
     * line breaks to survive. The value is typed into a theme-editor textarea
     * and then carried in an HTML attribute, and if those newlines are ever
     * normalised away a plain split('\n') would collapse the whole board into
     * one nonsense entry. Commas and semicolons work as separators too, so a
     * merchant typing it on one line still gets what they meant.
     */
    const seeds = (String(this.dataset.seedScores || '').match(/[^|\n,;]+\|\s*\d+/g) || [])
      .map((pair) => {
        const [name, value] = pair.split('|');
        return {
          name: name.trim().toUpperCase().slice(0, 7) || '???',
          score: Number(String(value).trim()) || 0,
        };
      })
      .filter((entry) => entry.score > 0);

    return [...seeds, ...mine]
      .filter((entry) => entry && Number.isFinite(entry.score))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }

  /**
   * Does this run earn a place on the board? Measured against the merged board
   * (seeds included), so the seeded names are real competition rather than
   * decoration — a visitor has to actually beat one to get their name up.
   */
  qualifies() {
    if (!this.score || this.score <= 0) return false;
    const board = this.readScores();
    return board.length < 3 || this.score > board[board.length - 1].score;
  }

  saveScore(score, name) {
    if (score <= 0) return;
    let mine = [];
    try {
      mine = JSON.parse(localStorage.getItem(SCORES_KEY) || '[]') || [];
    } catch (error) {
      mine = [];
    }
    mine.push({ name: (name || 'YOU').slice(0, ENTRY_SLOTS).toUpperCase(), score });
    mine.sort((a, b) => b.score - a.score);
    try {
      localStorage.setItem(SCORES_KEY, JSON.stringify(mine.slice(0, 5)));
    } catch (error) {
      /* storage unavailable; the run simply is not recorded. */
    }
  }

  /* ============================================================== game == */

  startGame() {
    // The prize is only ever offered after it has been earned, so a new run
    // always starts with the claim button put away — including a replay that
    // follows a win.
    this.claimBtn?.setAttribute('hidden', '');
    this.revealed = false;

    this.grid = Array.from({ length: ROWS }, () => new Array(COLS).fill(0));
    this.score = 0;
    this.lines = 0;
    this.level = Number(this.dataset.startLevel) || 0;
    this.startLevel = this.level;
    this.unlocked = false;
    this.paused = false;
    this.gravity = 0;
    this.das = null;
    // Normally set up by wireButtons(); guaranteed here so the update loop can
    // never meet an undefined held-key map.
    this.held = this.held || {};
    this.softFrames = 0;
    this.bag = [];
    this.next = this.pull();
    this.spawn();

    this.state = 'play';
    this.frame = 0;
    this.startMusic('game');
    this.say(`Game started. Score ${this.unlockAt} to unlock the reward.`);
  }

  /**
   * Seven-bag randomiser: every piece appears once per seven before any
   * repeats. The original's simpler random could starve you of a straight
   * piece for twenty drops, which reads as the game cheating rather than as
   * difficulty — and this popup gets one run to feel good.
   */
  pull() {
    if (!this.bag.length) {
      this.bag = PIECE_KEYS.slice();
      for (let i = this.bag.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.bag[i], this.bag[j]] = [this.bag[j], this.bag[i]];
      }
    }
    return this.bag.pop();
  }

  spawn() {
    this.piece = {
      key: this.next,
      rot: 0,
      x: 3,
      y: 0,
    };
    this.next = this.pull();
    // A fresh piece starts its gravity clock from zero, so it gets a full
    // interval before the first drop rather than inheriting the last one.
    this.gravity = 0;

    // No room for the new piece: the stack reached the ceiling.
    if (this.collides(this.piece.x, this.piece.y, this.piece.rot)) this.topOut();
  }

  cells(piece) {
    return PIECES[piece.key].states[piece.rot % PIECES[piece.key].states.length];
  }

  collides(x, y, rot) {
    const test = { key: this.piece.key, rot };
    const cells = this.cells(test);
    for (let i = 0; i < cells.length; i += 1) {
      const cx = x + cells[i][0];
      const cy = y + cells[i][1];
      if (cx < 0 || cx >= COLS || cy >= ROWS) return true;
      if (cy >= 0 && this.grid[cy][cx]) return true;
    }
    return false;
  }

  move(dir) {
    if (!this.piece || this.paused) return false;
    if (this.collides(this.piece.x + dir, this.piece.y, this.piece.rot)) return false;
    this.piece.x += dir;
    this.blip('move');
    return true;
  }

  /**
   * Rotate, with a one-column nudge if the wall is in the way. The original had
   * no kick at all — but it also had a player who owned the cartridge. Someone
   * meeting this for ninety seconds reads a dead rotate button as broken, so
   * the wall gets one column of give (two for the long piece).
   */
  rotate(dir) {
    if (!this.piece || this.paused) return;
    const states = PIECES[this.piece.key].states.length;
    const rot = (this.piece.rot + dir + states) % states;
    const kicks = this.piece.key === 'I' ? [0, -1, 1, -2, 2] : [0, -1, 1];

    for (let i = 0; i < kicks.length; i += 1) {
      if (!this.collides(this.piece.x + kicks[i], this.piece.y, rot)) {
        this.piece.x += kicks[i];
        this.piece.rot = rot;
        this.blip('rotate');
        return;
      }
    }
  }

  /** Down one row. Returns false when the piece has landed. */
  drop() {
    if (this.collides(this.piece.x, this.piece.y + 1, this.piece.rot)) return false;
    this.piece.y += 1;
    return true;
  }

  hardDrop() {
    if (!this.piece || this.paused) return;
    let travelled = 0;
    while (this.drop()) travelled += 1;
    this.addScore(travelled); // one point a row, same as a soft drop
    this.lockPiece();
  }

  /**
   * EVERY point goes through here. The reward is a score threshold, so a route
   * that adds points without testing it is a reward that silently never
   * arrives: hard drops used to add their rows and go straight to lockPiece,
   * and a player who crossed the threshold on dropped rows alone — easy at
   * 100, six pieces slammed to the floor — kept playing with the reward still
   * locked until a line clear or a soft-drop point happened to re-test it.
   */
  addScore(points) {
    this.score += points;
    this.checkUnlock();
  }

  lockPiece() {
    const cells = this.cells(this.piece);
    cells.forEach(([cx, cy]) => {
      const x = this.piece.x + cx;
      const y = this.piece.y + cy;
      if (y >= 0 && y < ROWS && x >= 0 && x < COLS) {
        this.grid[y][x] = PIECES[this.piece.key].style + 1;
      }
    });

    // State first, sound second. Committing the piece must not sit behind a
    // call that only makes a noise — that ordering is what turned one audio
    // fault into a game that stopped dead on its first landed piece.
    this.piece = null;
    this.blip('lock');

    const full = [];
    for (let y = 0; y < ROWS; y += 1) {
      if (this.grid[y].every((cell) => cell)) full.push(y);
    }

    if (full.length) {
      this.clearing = full;
      this.state = 'clearing';
      this.frame = 0;
      this.blip(full.length === 4 ? 'quad' : 'clear');
      return;
    }
    this.spawn();
  }

  /** Blink finished: drop everything above each cleared row and pay out. */
  collapse() {
    const rows = this.clearing;
    rows
      .slice()
      .sort((a, b) => a - b)
      .forEach((y) => {
        this.grid.splice(y, 1);
        this.grid.unshift(new Array(COLS).fill(0));
      });

    const before = this.level;
    this.lines += rows.length;
    this.addScore(LINE_SCORE[rows.length] * (this.level + 1));
    this.level = clamp(
      this.startLevel + Math.floor(this.lines / LINES_PER_LEVEL),
      0,
      GRAVITY.length - 1
    );
    if (this.level > before) {
      this.blip('level');
      this.retempoMusic(); // the loop tightens with the gravity
    }

    this.clearing = null;
    this.state = 'play';
    this.spawn();
  }

  /**
   * The twist. Cross the threshold and the reward is banked immediately — the
   * side panel says so and it cannot be lost. The run keeps going because the
   * prize is not the ending, topping out is; this just means the ending is
   * worth reaching.
   */
  checkUnlock() {
    if (this.unlocked || this.score < this.unlockAt) return;
    this.unlocked = true;
    this.unlockFrame = 0;
    this.blip('unlock');
    // Deliberately does not name the prize — it is revealed at the launch, and
    // a screen reader announcing it here would spoil the one surprise for the
    // people relying on it.
    this.say('Reward unlocked. Keep playing until you top out to reveal it.');
  }

  topOut() {
    this.piece = null;
    this.state = 'curtain';
    this.frame = 0;
    this.curtainRow = ROWS - 1;
    this.blip('over');
    // Saving happens at the initials screen, which only a qualifying run
    // reaches — so a score that misses the board is never recorded.
    this.stopMusic();
    this.say(
      this.unlocked
        ? `Game over with ${this.score} points. Reward unlocked.`
        : `Game over with ${this.score} points. Try again.`
    );
  }

  /* ============================================================== loop == */

  loop() {
    const tick = (now) => {
      this.raf = requestAnimationFrame(tick);
      if (this.last === undefined) this.last = now;

      // Cap the catch-up so returning to a backgrounded tab does not simulate
      // a thousand frames at once and instantly top the player out.
      let steps = Math.floor((now - this.last) / STEP);
      if (steps <= 0) return;
      this.last += steps * STEP;
      steps = Math.min(steps, MAX_CATCHUP);

      for (let i = 0; i < steps; i += 1) this.update();
      this.draw();
    };
    this.raf = requestAnimationFrame(tick);
  }

  update() {
    this.frame += 1;
    if (this.unlocked) this.unlockFrame = (this.unlockFrame || 0) + 1;

    if (this.state === 'boot') {
      this.updateBoot();
    } else if (this.state === 'play') {
      this.updatePlay();
    } else if (this.state === 'clearing') {
      if (this.frame >= CLEAR_FRAMES) this.collapse();
    } else if (this.state === 'curtain') {
      this.updateCurtain();
    } else if (this.state === 'rocket') {
      this.updateRocket();
    }
  }

  updateBoot() {
    this.bootY = Math.min(this.bootY + 1.4, 58);
    if (this.bootY >= 58 && !this.chimed) {
      this.chimed = true;
      this.chime();
    }
    if (this.frame > 132) this.toTitle();
  }

  updatePlay() {
    if (this.paused || !this.piece) return;

    // Held direction: a long first beat, then a fast walk.
    if (this.das) {
      const stillHeld = this.das.dir < 0 ? this.held.left : this.held.right;
      if (!stillHeld) {
        this.das = null;
      } else {
        this.das.timer -= 1;
        if (this.das.timer <= 0) {
          this.move(this.das.dir);
          this.das.timer = DAS_REPEAT;
        }
      }
    }

    const speed = GRAVITY[this.level] || 3;
    const soft = this.held.down;

    this.gravity += 1;
    // Soft drop runs at a fixed fast rate, or gravity if that is already faster.
    if (this.gravity >= (soft ? Math.min(2, speed) : speed)) {
      this.gravity = 0;
      if (this.drop()) {
        if (soft) {
          this.addScore(1); // a point a row, the classic soft-drop bonus
        }
      } else {
        // Nowhere left to fall: commit it here and now. No grace period — a
        // piece resting on the stack is spent, which is what makes a misplaced
        // one actually cost something.
        this.lockPiece();
      }
    }
  }

  /** The game-over curtain: the field fills from the bottom up, then the panel. */
  updateCurtain() {
    if (this.frame % 3 !== 0) return;
    if (this.curtainRow >= 0) {
      this.grid[this.curtainRow].fill(3);
      this.curtainRow -= 1;
      return;
    }
    // Curtain finished. A run good enough for the board gets its initials in
    // first — the arcade order, and it reads as a bigger deal than the reward
    // screen quietly listing a number.
    if (this.qualifies()) {
      this.state = 'entry';
      this.frame = 0;
      this.entryName = new Array(ENTRY_SLOTS).fill('A');
      this.entryPos = 0;
      this.say(`New high score, ${this.score}. Enter your initials.`);
      return;
    }
    this.afterCurtain();
  }

  /** Where a finished run goes: the reward if it was earned, otherwise a retry. */
  afterCurtain() {
    if (this.unlocked) {
      this.state = 'rocket';
      this.frame = 0;
      this.rocketY = 0;
      this.launch();
    } else {
      this.state = 'over';
      this.frame = 0;
    }
  }

  /** Spin one initial up or down through the alphabet, wrapping at both ends. */
  cycleLetter(step) {
    const i = ENTRY_ALPHABET.indexOf(this.entryName[this.entryPos]);
    const next = (i + step + ENTRY_ALPHABET.length) % ENTRY_ALPHABET.length;
    this.entryName[this.entryPos] = ENTRY_ALPHABET[next];
    this.blip('move');
  }

  confirmName() {
    const name = this.entryName.join('').trim() || 'YOU';
    this.saveScore(this.score, name);
    this.blip('unlock');
    this.say(`Saved as ${name}.`);
    this.afterCurtain();
  }

  /* ============================================================ reward == */

  /**
   * Arm the launch. Resets the reveal so a visitor who plays a second run does
   * not meet an already-revealed prize and an already-live claim button.
   */
  launch() {
    this.revealed = false;
    this.claimBtn?.setAttribute('hidden', '');
  }

  /**
   * The payoff. Countdown, ignition, then the rocket climbs off the top of the
   * screen and the discount is left standing in the starfield. Only ever
   * reached with the reward already banked, so nothing here can disappoint.
   */
  updateRocket() {
    const f = this.frame;
    if (f < 90) return; // countdown 3, 2, 1 — anticipation is most of the trick

    const t = (f - 90) / 60;
    this.rocketY = t * t * 34; // accelerating, the way a launch actually looks

    if (f === 90) this.blip('ignite');
    if (f > 200 && !this.revealed) {
      this.revealed = true;
      this.blip('reveal');
      this.claimBtn?.removeAttribute('hidden');
      this.say(`${this.prize} unlocked. Claim it below.`);
    }
  }

  claim() {
    writeState({ claimed: true });

    const formId = (this.dataset.formId || '').trim();
    // Omnisend's documented custom trigger. The snippet ships with the app embed.
    if (formId && window.omnisend) {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['openForm', formId]);
      // Give the form a beat to mount before our dialog gets out of its way.
      window.setTimeout(() => this.close(), 400);
      return;
    }

    if (formId) {
      console.warn('Anemoia Boy: Omnisend snippet not found; using the fallback step.');
    }
    this.showStep(this.dataset.workerUrl ? 'email' : 'done');
  }

  /**
   * Fallback email capture, reached only when the Omnisend snippet is missing
   * (app embed off, or a blocker ate it) AND a Worker URL is configured. Posts
   * to the same Cloudflare Worker as the waitlist form.
   */
  wireForm() {
    this.form = this.querySelector('[data-agb-form]');
    if (!this.form) return;

    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const errorEl = this.querySelector('[data-agb-error]');
      if (errorEl) errorEl.textContent = '';

      // Honeypot: a filled "company" means a bot — swallow it silently.
      if (this.form.company && this.form.company.value.trim() !== '') {
        this.showStep('done');
        return;
      }
      if (!this.form.reportValidity()) return;

      const submit = this.querySelector('[data-agb-submit]');
      if (submit) submit.disabled = true;

      try {
        const response = await fetch(this.dataset.workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: this.form.email.value.trim(),
            source: 'anemoia-gameboy',
            product: 'handheld game',
            score: this.score || 0,
            pageUrl: window.location.href,
          }),
        });
        if (!response.ok) throw new Error(`Signup failed (${response.status})`);
        this.showStep('done');
      } catch (error) {
        console.error('Anemoia Boy signup failed', error);
        if (errorEl) errorEl.textContent = 'Something went wrong. Please try again in a moment.';
      } finally {
        if (submit) submit.disabled = false;
      }
    });
  }

  /* ============================================================== draw == */

  /**
   * Four shades, read off CSS custom properties so the LCD follows SOLAR/LUNAR
   * with the rest of the site. Index 0 is the lit background, 3 the darkest ink.
   */
  readPalette() {
    const styles = getComputedStyle(this);
    this.pal = [0, 1, 2, 3].map(
      (i) => styles.getPropertyValue(`--agb-lcd-${i}`).trim() || ['#9bbc0f', '#8bac0f', '#306230', '#0f380f'][i]
    );
  }

  px(x, y, w, h, color) {
    this.ctx.fillStyle = this.pal[color];
    this.ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  /** 1px outlined rectangle. */
  frameRect(x, y, w, h, color) {
    this.px(x, y, w, 1, color);
    this.px(x, y + h - 1, w, 1, color);
    this.px(x, y, 1, h, color);
    this.px(x + w - 1, y, 1, h, color);
  }

  text(str, x, y, color, scale = 1) {
    const chars = String(str).toUpperCase().split('');
    let cx = x;
    chars.forEach((char) => {
      const glyph = FONT[char];
      if (glyph) {
        this.ctx.fillStyle = this.pal[color];
        for (let row = 0; row < GLYPH_H; row += 1) {
          const bits = glyph[row];
          for (let col = 0; col < GLYPH_W; col += 1) {
            // Bit 4 is the leftmost pixel of a 5-wide glyph.
            if (bits & (1 << (GLYPH_W - 1 - col))) {
              this.ctx.fillRect(cx + col * scale, y + row * scale, scale, scale);
            }
          }
        }
      }
      cx += (GLYPH_W + TRACK) * scale;
    });
  }

  textWidth(str, scale = 1) {
    const len = String(str).length;
    return len * (GLYPH_W + TRACK) * scale - TRACK * scale;
  }

  centerText(str, y, color, scale = 1, within = W) {
    this.text(str, Math.round((within - this.textWidth(str, scale)) / 2), y, color, scale);
  }

  /**
   * One stack block. Three textures so a field of four shades still reads as
   * distinct pieces — the original solved the same problem the same way.
   */
  block(x, y, style) {
    this.px(x, y, CELL, CELL, 3);
    this.px(x + 1, y + 1, CELL - 2, CELL - 2, 1);
    if (style === 1) {
      this.px(x + 1, y + 1, 2, 2, 0);
    } else if (style === 2) {
      this.px(x + 2, y + 2, 3, 3, 3);
    } else {
      this.px(x + 1, y + 3, CELL - 2, 1, 3);
    }
  }

  draw() {
    this.px(0, 0, W, H, 0);
    if (this.state === 'boot') this.drawBoot();
    else if (this.state === 'title') this.drawTitle();
    else if (this.state === 'entry') this.drawEntry();
    else if (this.state === 'rocket') this.drawRocket();
    else this.drawGame();
  }

  /**
   * Initials entry. Three big slots with the active one underlined, blinking so
   * it is obvious which one the D-pad is currently spinning.
   */
  drawEntry() {
    this.centerText('NEW HIGH SCORE', 14, 3, 1);
    this.centerText(pad(this.score, 6), 28, 3, 2);
    this.px(20, 50, W - 40, 1, 2);
    this.centerText('ENTER YOUR NAME', 58, 2, 1);

    const scale = 3;
    const step = (GLYPH_W + TRACK) * scale;
    const startX = Math.round((W - (step * ENTRY_SLOTS - TRACK * scale)) / 2);
    const y = 74;

    for (let i = 0; i < ENTRY_SLOTS; i += 1) {
      const x = startX + i * step;
      this.text(this.entryName[i], x, y, 3, scale);

      // Every slot carries a faint rule so they all read as fillable; the
      // active one blinks solid on top of it.
      const active = i === this.entryPos;
      const lit = active && Math.floor(this.frame / 15) % 2 === 0;
      this.px(x, y + GLYPH_H * scale + 3, GLYPH_W * scale, 2, lit ? 3 : 1);
    }

    this.centerText('UP DOWN TO CHANGE', 116, 1, 1);
    this.centerText('A TO CONFIRM', 128, 2, 1);
  }

  drawBoot() {
    const y = Math.round(this.bootY);
    this.centerText(this.gameSuper, y, 3, 2);
    if (this.chimed) this.centerText(this.gameName, y + 20, 2, 1);
  }

  drawTitle() {
    this.centerText(this.gameSuper, 12, 2, 1);
    this.centerText(this.gameName, 24, 3, 2);
    this.px(28, 44, W - 56, 1, 2);

    // Top three, local. Names are clipped to seven so the columns never collide.
    this.centerText('TOP SCORES', 50, 2, 1);
    const scores = this.scores || [];
    for (let i = 0; i < 3; i += 1) {
      const entry = scores[i];
      const y = 62 + i * 10;
      if (!entry) {
        this.text(`${i + 1}  -------  ------`, 22, y, 1, 1);
        continue;
      }
      const name = String(entry.name).slice(0, 7).padEnd(7, ' ');
      this.text(`${i + 1}  ${name}  ${pad(entry.score, 6)}`, 22, y, 3, 1);
    }

    // The twist, and the whole reason the console is on screen. As a plain line
    // of text it sat in the same shade as the three score rows above it and read
    // as more leaderboard, so it is an inverted plate instead: the target score
    // at double height, knocked out of solid ink. It snaps to reverse video for
    // 8 frames of every 48 — a blink roughly every 0.8s, offset from the slower
    // PRESS START pulse below so the two never flash together.
    const flash = this.frame % 48 < 8;
    const plate = flash ? 0 : 3; // ground: dark normally, lit on the flash
    const ink = flash ? 3 : 0; // and the type the other way round
    const sub = flash ? 3 : 1;
    this.px(6, 92, W - 12, 32, plate);
    this.frameRect(6, 92, W - 12, 32, ink);
    this.centerText(`SCORE ${this.unlockAt}`, 96, ink, 2);
    this.centerText('TO UNLOCK A REWARD', 113, sub, 1);

    // Blink at 1Hz. The pause is what makes the prompt read as an invitation.
    if (Math.floor(this.frame / 30) % 2 === 0) {
      this.centerText('PRESS START', 130, 3, 1);
    }
  }

  drawGame() {
    // Playfield frame.
    this.frameRect(FX - 1, FY - 1, COLS * CELL + 2, ROWS * CELL + 2, 3);

    for (let y = 0; y < ROWS; y += 1) {
      // A clearing row blinks: the pause before the stack falls is the reward
      // for making the line, and without it a four-line clear is invisible.
      const isClearing = this.clearing && this.clearing.includes(y);
      if (isClearing && Math.floor(this.frame / 4) % 2 === 0) continue;

      for (let x = 0; x < COLS; x += 1) {
        const cell = this.grid[y][x];
        if (cell) this.block(FX + x * CELL, FY + y * CELL, cell - 1);
      }
    }

    if (this.piece) {
      const style = PIECES[this.piece.key].style;
      this.cells(this.piece).forEach(([cx, cy]) => {
        const y = this.piece.y + cy;
        if (y < 0) return; // still above the ceiling on spawn
        this.block(FX + (this.piece.x + cx) * CELL, FY + y * CELL, style);
      });
    }

    this.drawPanel();

    if (this.paused) this.drawBanner('PAUSED', '');
    if (this.state === 'over') {
      this.drawBanner('GAME OVER', this.unlocked ? '' : 'TRY AGAIN');
    }
  }

  drawPanel() {
    const x = PANEL_X;
    this.text('SCORE', x, 10, 2);
    this.text(pad(this.score || 0, 6), x, 19, 3);
    this.text('LEVEL', x, 31, 2);
    // Displayed one-indexed. The level is genuinely 0-based internally (it
    // indexes the gravity table and multiplies line payouts as level+1), but a
    // panel reading LEVEL 00 on the first piece looks broken rather than
    // starting, so the player is shown 01 upwards.
    this.text(pad((this.level || 0) + 1, 2), x, 40, 3);
    this.text('LINES', x, 52, 2);
    this.text(pad(this.lines || 0, 3), x, 61, 3);

    this.text('NEXT', x, 73, 2);
    this.frameRect(x, 81, 30, 30, 3);
    if (this.next && this.state === 'play') {
      const cells = PIECES[this.next].states[0];
      const style = PIECES[this.next].style;
      // Centre the piece in its box off its own bounding width, so the flat
      // long piece and the square both sit centred rather than left-aligned.
      const xs = cells.map((c) => c[0]);
      const ys = cells.map((c) => c[1]);
      const ox = x + 15 - ((Math.min(...xs) + Math.max(...xs) + 1) * CELL) / 2;
      const oy = 96 - ((Math.min(...ys) + Math.max(...ys) + 1) * CELL) / 2;
      cells.forEach(([cx, cy]) => this.block(ox + cx * CELL, oy + cy * CELL, style));
    }

    // The reward notice. Blinks for its first couple of seconds to catch the
    // eye mid-run, then settles into a steady badge so it reads as banked.
    //
    // It says only that something was won, never WHAT. The prize itself is held
    // back for the rocket, so the launch has something left to reveal — telling
    // them here would make the whole cutscene a restatement.
    if (!this.unlocked) return;
    const fresh = (this.unlockFrame || 0) < 150;
    if (fresh && Math.floor(this.frame / 6) % 2 === 0) return;
    this.text('REWARD', x, 120, 3);
    this.text('UNLOCKED', x, 129, 3);
  }

  /** A boxed message across the playfield: pause, and the plain game over. */
  drawBanner(title, sub) {
    const w = COLS * CELL + 2;
    const y = 52;
    const h = sub ? 34 : 22;
    this.px(FX - 1, y, w, h, 0);
    this.frameRect(FX - 1, y, w, h, 3);
    this.centerText(title, y + 7, 3, 1, w + (FX - 1) * 2);
    if (sub) this.centerText(sub, y + 20, 2, 1, w + (FX - 1) * 2);
  }

  /* ------------------------------------------------------------ rocket -- */

  drawRocket() {
    const f = this.frame;
    const lift = this.rocketY || 0;

    // Starfield, scrolling down past the climb. Deterministic from the index so
    // the stars hold still between frames instead of boiling.
    for (let i = 0; i < 26; i += 1) {
      const sx = (i * 37) % W;
      const sy = ((i * 53 + Math.floor(lift * 2)) % (H + 20)) - 10;
      this.px(sx, sy, 1, 1, i % 3 === 0 ? 2 : 1);
    }

    // Ground and pad, sliding away under the rocket.
    const ground = 120 + lift;
    if (ground < H) {
      this.px(0, ground, W, H - ground, 2);
      this.px(0, ground, W, 1, 3);
      this.px(60, ground - 10, 3, 10, 3);
      this.px(97, ground - 10, 3, 10, 3);
    }

    const rx = 72;
    const ry = 84 - lift;

    // Exhaust, once lit. Flickers on a 2-frame cycle so it reads as fire.
    if (f >= 90) {
      const flame = 6 + (f % 4) * 2 + Math.min(lift, 20);
      this.px(rx + 3, ry + 26, 10, flame, 2);
      this.px(rx + 5, ry + 26, 6, flame + 4, 3);
      this.px(rx + 7, ry + 26, 2, flame + 8, 1);
    }

    // The rocket: nose, body, fins, window.
    this.px(rx + 6, ry, 4, 4, 3);
    this.px(rx + 5, ry + 3, 6, 4, 3);
    this.px(rx + 4, ry + 6, 8, 20, 3);
    this.px(rx + 5, ry + 8, 6, 16, 1);
    this.px(rx + 6, ry + 10, 4, 4, 0);
    this.px(rx + 1, ry + 18, 3, 8, 3);
    this.px(rx + 12, ry + 18, 3, 8, 3);
    this.px(rx + 4, ry + 26, 8, 2, 2);

    if (f < 90) {
      // 3, 2, 1 — one second each.
      const count = 3 - Math.floor(f / 30);
      this.centerText(String(count), 20, 3, 3);
      this.centerText('LAUNCHING YOUR REWARD', 8, 2, 1);
      return;
    }

    if (!this.revealed) return;
    // The reveal, on a plate so it stays legible over the starfield.
    this.px(8, 40, W - 16, 46, 0);
    this.frameRect(8, 40, W - 16, 46, 3);
    this.centerText('REWARD UNLOCKED', 47, 2, 1);
    this.centerText(this.prize, 60, 3, 2);
    this.centerText(`SCORE ${pad(this.score, 6)}`, 76, 1, 1);
  }

  /* =============================================================== audio == */
  /*
   * Everything is synthesised. The original hardware had four channels, two of
   * them plain square waves, and that timbre is most of why these games sound
   * the way they do — so square is the default and there is nothing to upload.
   *
   * All of it respects the site-wide sound toggle: a visitor who has not asked
   * for sound gets a silent handheld.
   */

  /*
   * NOTE THE NAME: `actx`, never `ctx`. `this.ctx` is the canvas 2D context and
   * has been since connectedCallback. Reusing that name here handed every sound
   * call the canvas instead of an AudioContext, so `createOscillator` was not a
   * function and blip() threw — which, because blip('lock') runs inside
   * lockPiece() before spawn(), silently killed the game on the first landed
   * piece whenever sound was on.
   */
  audio() {
    if (this.actx !== undefined) return this.actx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      this.actx = null;
      return null;
    }
    try {
      this.actx = new Ctx();

      this.bus = this.actx.createGain();
      this.bus.gain.value = 1;

      /*
       * A limiter on the end of the bus. Phone speakers are quiet, so the
       * levels below are pushed harder than a desktop mix would need; this
       * catches the peaks when several effects and both music voices land on
       * the same frame, instead of letting them clip.
       */
      const limiter = this.actx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 0;
      limiter.ratio.value = 20;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.1;

      this.bus.connect(limiter);
      this.routeOutput(limiter);

      // iOS parks the context again on any interruption — a call, Siri, the
      // page going to the background. Nothing else asks it to come back.
      this.actx.addEventListener?.('statechange', () => {
        if (this.actx && this.actx.state !== 'running' && !this.hidden) {
          this.actx.resume?.()?.catch?.(() => {});
        }
      });
    } catch (error) {
      this.actx = null;
    }
    return this.actx;
  }

  /**
   * Where the finished mix goes.
   *
   * On iOS, WebAudio and media elements sit on DIFFERENT audio sessions.
   * WebAudio counts as UI sound and is silenced by the hardware ringer switch;
   * an <audio> element counts as media and plays regardless. The rest of this
   * storefront is built on media elements (see anemoia-world.js) which is
   * exactly why every other page stayed audible on an iPhone while this console
   * — pure synthesis — did not.
   *
   * So on iOS the mix is piped into a MediaStream and played back through an
   * <audio> element, which puts it on the same session as everything else.
   * Everywhere else the direct connection is already proven, so it is left
   * alone rather than risking a working path for a fix nobody there needs.
   *
   * The element is appended inside this component on purpose: hushPage() skips
   * anything `this.contains()`, so silencing the page cannot silence the game.
   */
  routeOutput(node) {
    const ctx = this.actx;
    const iOS =
      /iP(hone|ad|od)/.test(navigator.platform || '') ||
      (/Mac/.test(navigator.userAgent || '') && 'ontouchend' in document);

    if (iOS && ctx.createMediaStreamDestination) {
      try {
        const dest = ctx.createMediaStreamDestination();
        node.connect(dest);

        const el = document.createElement('audio');
        el.srcObject = dest.stream;
        el.playsInline = true;
        el.setAttribute('playsinline', '');
        el.preload = 'auto';
        this.appendChild(el);

        this.sink = el;
        return;
      } catch (error) {
        /* fall through to the direct connection below */
      }
    }

    node.connect(ctx.destination);
    this.sink = null;
  }

  /**
   * Wake the audio context, and on the first call actually unlock output.
   *
   * Two iOS-specific things going on:
   *
   *   1. `interrupted` is an iOS-only context state (phone call, Siri, the tab
   *      backgrounding). It is not in the spec, so the usual
   *      `state === 'suspended'` check sails straight past it and the console
   *      stays silent for the rest of the session.
   *   2. resume() alone is not always enough. iOS will hand back a context that
   *      reports `running` and still plays nothing until a source has been
   *      STARTED inside a real user gesture. Playing one silent sample is the
   *      long-standing way to get output actually opened.
   *
   * Must therefore be called from a gesture handler, synchronously — which is
   * why it runs on the YES click, on every button press, and on the sound
   * switch, rather than once at startup.
   */
  resumeAudio() {
    const ctx = this.audio();
    if (!ctx) return;

    if (ctx.state !== 'running') {
      ctx.resume?.()?.catch?.(() => {});
    }

    // The media-element sink (iOS) has its own autoplay gate, and it is a
    // separate one from the context's. It has to be started from inside a
    // gesture too, and re-started after any interruption — so this runs on
    // every press, not just the first.
    if (this.sink && this.sink.paused) {
      this.sink.play()?.catch?.(() => {});
    }

    if (this.audioUnlocked) return;
    this.audioUnlocked = true;
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (error) {
      /* unlock failed; the context may still work on its own */
    }
  }

  /**
   * One square-wave note. `at` offsets from now, for building phrases.
   *
   * Wrapped, and it must stay wrapped: sound is decoration and gameplay is not.
   * Several blips are fired from inside state transitions — blip('lock') runs in
   * lockPiece() before the next piece spawns — so anything that throws in here
   * takes the whole game down with it. An audio failure should cost the player
   * a sound effect, nothing more.
   */
  note(freq, duration, { at = 0, type = 'square', gain = 0.06 } = {}) {
    if (!soundOn()) return;
    try {
      const ctx = this.audio();
      if (!ctx || !this.bus) return;
      const t = ctx.currentTime + at;

      const level = gain * MASTER_GAIN;
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t);
      // A hard attack and a quick decay: the envelope of a chip, not a synth pad.
      amp.gain.setValueAtTime(0.0001, t);
      amp.gain.linearRampToValueAtTime(level, t + 0.005);
      amp.gain.exponentialRampToValueAtTime(0.0001, t + duration);
      osc.connect(amp).connect(this.bus);
      osc.start(t);
      osc.stop(t + duration + 0.02);
    } catch (error) {
      /* audio is optional; never let it break the run */
    }
  }

  /** Play a phrase of [frequency, seconds] pairs back to back. */
  phrase(notes, options = {}) {
    let at = 0;
    notes.forEach(([freq, dur]) => {
      if (freq) this.note(freq, dur * 0.9, { ...options, at });
      at += dur;
    });
  }

  blip(kind) {
    if (!soundOn()) return;
    switch (kind) {
      case 'move':
        this.note(220, 0.03, { gain: 0.04 });
        break;
      case 'rotate':
        this.note(330, 0.035, { gain: 0.045 });
        break;
      case 'lock':
        this.note(150, 0.06, { type: 'triangle', gain: 0.07 });
        break;
      case 'clear':
        this.phrase([[523, 0.06], [659, 0.06], [784, 0.09]]);
        break;
      case 'quad':
        // Four rows at once deserves more than the single-line chirp.
        this.phrase([[523, 0.05], [659, 0.05], [784, 0.05], [1047, 0.05], [1319, 0.14]]);
        break;
      case 'level':
        this.phrase([[784, 0.07], [1047, 0.12]]);
        break;
      case 'unlock':
        this.phrase([[659, 0.07], [880, 0.07], [1109, 0.07], [1319, 0.2]], { gain: 0.07 });
        break;
      case 'over':
        this.phrase([[392, 0.12], [349, 0.12], [311, 0.12], [262, 0.35]], { type: 'triangle' });
        break;
      case 'ignite':
        this.rumble();
        break;
      case 'reveal':
        this.phrase(
          [[523, 0.09], [659, 0.09], [784, 0.09], [1047, 0.09], [784, 0.09], [1047, 0.36]],
          { gain: 0.075 }
        );
        break;
      default:
        break;
    }
  }

  /** Power-on: two notes, an octave apart. */
  chime() {
    this.phrase([[784, 0.1], [1568, 0.4]], { gain: 0.07 });
  }

  /** Launch: filtered noise swelling under a rising sweep. */
  rumble() {
    if (!soundOn()) return;
    try {
      this.buildRumble();
    } catch (error) {
      /* audio is optional; the launch still happens silently */
    }
  }

  buildRumble() {
    const ctx = this.audio();
    if (!ctx || !this.bus) return;
    const t = ctx.currentTime;
    const seconds = 2.4;

    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const band = ctx.createBiquadFilter();
    band.type = 'lowpass';
    band.frequency.setValueAtTime(300, t);
    band.frequency.linearRampToValueAtTime(1400, t + seconds);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.linearRampToValueAtTime(0.12, t + 0.5);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + seconds);
    src.connect(band).connect(amp).connect(this.bus);
    src.start(t);

    const sweep = ctx.createOscillator();
    const sweepAmp = ctx.createGain();
    sweep.type = 'sawtooth';
    sweep.frequency.setValueAtTime(60, t);
    sweep.frequency.exponentialRampToValueAtTime(520, t + seconds);
    sweepAmp.gain.setValueAtTime(0.04, t);
    sweepAmp.gain.exponentialRampToValueAtTime(0.0001, t + seconds);
    sweep.connect(sweepAmp).connect(this.bus);
    sweep.start(t);
    sweep.stop(t + seconds);
  }

  /**
   * Start a looping track. Both loops are ours and written to be forgettable —
   * a lead line over a bass pulse, scheduled a bar at a time on the same square
   * oscillator as the effects. They have to survive being heard twenty times
   * without becoming irritating, which rules out anything memorable.
   *
   * Gameplay tempo tightens with the level, so the music tenses up alongside
   * the gravity rather than staying placid while the field fills.
   */
  startMusic(track = 'title') {
    this.stopMusic();
    this.musicTrack = track;
    if (!soundOn() || !this.musicOn || REDUCED_MOTION.matches) return;

    const def = MUSIC[track];
    if (!def) return;

    const step =
      track === 'game'
        ? Math.max(0.085, def.step - (this.level || 0) * 0.003)
        : def.step;
    const bar = step * def.lead.length;

    const play = () => {
      def.lead.forEach((f, i) => {
        if (f) this.note(f, step * 0.85, { at: i * step, gain: def.leadGain });
      });
      def.bass.forEach((f, i) => {
        if (f) {
          this.note(f, step * 1.6, { at: i * step, type: 'triangle', gain: def.bassGain });
        }
      });
    };

    play();
    this.music = window.setInterval(play, bar * 1000);
  }

  /** Level changed: relaunch the loop so the tempo follows it. */
  retempoMusic() {
    if (this.music && this.musicTrack === 'game') this.startMusic('game');
  }

  stopMusic() {
    if (this.music) window.clearInterval(this.music);
    this.music = null;
  }

  /**
   * The console's own sound switch, writing the site-wide preference so it
   * stays in step with the rest of the storefront. Without this a visitor has
   * no way to hear any of it from inside the popup — the shared default is
   * silence, which for a handheld game means missing half the thing.
   */
  toggleSound() {
    const next = soundOn() ? 'off' : 'on';
    try {
      // Our own key, never the storefront's — see soundOn() for why.
      localStorage.setItem(GAME_SOUND_KEY, next);
    } catch (error) {
      /* storage unavailable; the toggle just will not persist. */
    }
    this.syncSoundBtn();

    if (next === 'on') {
      this.resumeAudio();
      this.blip('rotate'); // confirm audibly that it worked
      if (this.state === 'play') this.startMusic('game');
      else if (this.state === 'title') this.startMusic('title');
    } else {
      this.stopMusic();
    }
  }

  syncSoundBtn() {
    if (!this.soundBtn) return;
    const on = soundOn();
    this.soundBtn.textContent = on ? 'SOUND ON' : 'SOUND OFF';
    this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.soundBtn.classList.toggle('is-on', on);
  }
}

if (!customElements.get('anemoia-gameboy')) {
  customElements.define('anemoia-gameboy', AnemoiaGameboy);
}
